import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle, CheckCircle } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";

const WindRiskComponent = () => {
  const { projectId } = useParams();

  const [fileData, setFileData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [toast, setToast] = useState(null);

  const apiRoot = process.env.REACT_APP_API_ROOT;
  const userId = localStorage.getItem("userID");
  const token = localStorage.getItem("token");

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;
    setFileData(files[0]);
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setSubmitStatus(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!fileData) {
      showToast("Please select a file to upload", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("json_file", fileData);
      formData.append("project_id", projectId);

      const response = await fetch(
        `${apiRoot}/api/wind_risk/import_wind_risk_data/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload failed");
      }

      const data = await response.json();

      showToast(
        `${data.message} (Created: ${data.created}, Skipped: ${data.skipped})`,
        "success"
      );
      setFileData(null);
    } catch (error) {
      console.error("Error uploading file:", error);
      showToast(
        error.message || "Error uploading file. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Wind Risk Analysis Setup - Project ${projectId}`}
            subtitle="Configure wind risk analysis settings by uploading your data file"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Wind Risk Data File" />

                  <FileUploadZone
                    onFilesSelect={handleFilesSelect}
                    disabled={isSubmitting}
                    accept=".json"
                    acceptedTypes={["JSON"]}
                  />

                  {submitStatus === "success" && (
                    <div className="flex items-center gap-2 p-4 mt-4 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <p className="text-sm text-green-700">
                        File uploaded successfully!
                      </p>
                    </div>
                  )}

                  {submitStatus === "error" && (
                    <div className="flex items-center gap-2 p-4 mt-4 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <p className="text-sm text-red-700">
                        Error uploading file. Please try again.
                      </p>
                    </div>
                  )}

                  <div className="mt-6">
                    <PrimaryButton
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={!fileData}
                      loadingText="Uploading..."
                    >
                      Upload File
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="File Requirements" />
                  <ul className="space-y-2">
                    {[
                      "Upload a JSON file containing your wind risk analysis data",
                      "Ensure the JSON contains proper wind risk data for surfaces",
                      "File must include direction data and window frequency information",
                      "Each surface must already exist in the project",
                    ].map((requirement, index) => (
                      <li
                        key={index}
                        className="flex items-start text-sm text-gray-600"
                      >
                        <span className="mr-2">•</span>
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default WindRiskComponent;
